import React, { useState } from 'react';
import { BookOpen, Sparkles, ExternalLink, Tablet, BookMarked, ShoppingCart, ArrowRight, ZoomIn } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { useLang } from '../i18n';
import { BOOK, FORMATS, AMAZON_PAPERBACK, COVER_URL, CONTENT, trackBookClick } from '../bookData';

const FORMAT_ICONS = { ebook: Tablet, paperback: BookOpen, hardcover: BookMarked };

// Reusable cover visual: real image when COVER_URL is set, otherwise a tasteful
// stylised placeholder. `widthClass` sets the width; height follows the real
// cover aspect ratio (538x851) so nothing gets cropped. When `zoomable` and a
// real cover exist, clicking opens a large lightbox. Shared by banner + page.
export const BookCover = ({ className = '', widthClass = 'w-24', iconClass = 'h-9 w-9', zoomable = true }) => {
  const { lang } = useLang();
  const isDe = lang !== 'en';
  const [zoomOpen, setZoomOpen] = useState(false);

  if (COVER_URL) {
    const img = (
      <img
        src={COVER_URL}
        alt={`${BOOK.title} – ${BOOK.author}`}
        className={`${widthClass} aspect-[538/851] shrink-0 rounded-lg border border-border object-cover shadow-md ${className}`}
        loading="lazy"
        data-testid="book-cover-image"
      />
    );
    if (!zoomable) return img;
    return (
      <>
        <button
          type="button"
          onClick={() => setZoomOpen(true)}
          data-testid="book-cover-zoom-btn"
          aria-label={isDe ? 'Cover vergrößern' : 'Enlarge cover'}
          title={isDe ? 'Cover vergrößern' : 'Enlarge cover'}
          className={`group relative shrink-0 cursor-zoom-in rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${className}`}
        >
          <img
            src={COVER_URL}
            alt={`${BOOK.title} – ${BOOK.author}`}
            className={`${widthClass} aspect-[538/851] rounded-lg border border-border object-cover shadow-md transition-transform duration-150 group-hover:scale-[1.02]`}
            loading="lazy"
            data-testid="book-cover-image"
          />
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-foreground/0 opacity-0 transition-opacity duration-150 group-hover:bg-foreground/20 group-hover:opacity-100">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-background/90 text-foreground shadow-md">
              <ZoomIn className="h-4 w-4" />
            </span>
          </span>
        </button>

        <Dialog open={zoomOpen} onOpenChange={setZoomOpen}>
          <DialogContent
            className="max-w-[92vw] overflow-hidden border-0 bg-transparent p-0 shadow-none sm:max-w-md"
            data-testid="book-cover-zoom-dialog"
          >
            <DialogHeader className="sr-only">
              <DialogTitle>{BOOK.title}</DialogTitle>
              <DialogDescription>{isDe ? `Cover von ${BOOK.author}` : `Cover by ${BOOK.author}`}</DialogDescription>
            </DialogHeader>
            <img
              src={COVER_URL}
              alt={`${BOOK.title} – ${BOOK.author}`}
              className="mx-auto max-h-[85vh] w-auto rounded-xl shadow-2xl"
              data-testid="book-cover-zoom-image"
            />
          </DialogContent>
        </Dialog>
      </>
    );
  }
  return (
    <div
      aria-hidden="true"
      data-testid="book-cover-placeholder"
      className={`${widthClass} aspect-[538/851] flex shrink-0 flex-col items-center justify-center rounded-lg border border-primary/20 bg-gradient-to-br from-primary/15 via-primary/5 to-secondary/60 text-primary shadow-inner ${className}`}
    >
      <BookOpen className={iconClass} />
      <span className="mt-2 px-2 text-center text-[10px] font-semibold leading-tight text-foreground/70">
        {BOOK.title}
      </span>
    </div>
  );
};

// Opens the dedicated book landing page (root + ?buch=1 for reliable static hosting).
const goToBookPage = () => {
  try {
    window.location.href = '/?buch=1';
  } catch (e) { /* noop */ }
};

export const BookPromoBanner = ({ className = '', variant = 'full' }) => {
  const { lang } = useLang();
  const isDe = lang !== 'en';
  const [open, setOpen] = useState(false);
  const c = isDe ? CONTENT.de : CONTENT.en;

  const compact = variant === 'compact';

  return (
    <section
      className={className}
      aria-label={isDe ? 'Buchempfehlung' : 'Book recommendation'}
      data-testid={compact ? 'book-promo-banner-top' : 'book-promo-banner'}
    >
      <div className="relative overflow-hidden rounded-2xl border bg-card shadow-sm">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 blur-2xl"
        />
        <div className={`relative flex flex-col items-center gap-5 ${compact ? 'p-4 sm:flex-row sm:gap-4 sm:p-4' : 'p-6 sm:flex-row sm:items-center sm:gap-6 sm:p-7'}`}>
          <BookCover
            widthClass={compact ? 'w-16' : 'w-24'}
            iconClass={compact ? 'h-6 w-6' : 'h-9 w-9'}
          />

          <div className="min-w-0 flex-1 text-center sm:text-left">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary">
              <Sparkles className="h-3 w-3" />
              {isDe ? 'Buchtipp' : 'Book tip'}
            </span>
            <h3 className={`mt-2 font-heading font-semibold text-foreground ${compact ? 'text-base sm:text-lg' : 'text-lg sm:text-xl'}`}>
              {BOOK.title}
            </h3>
            <p className="text-sm font-medium text-muted-foreground">
              {isDe ? 'von' : 'by'} {BOOK.author}
            </p>
            {!compact && (
              <p className="mx-auto mt-1.5 max-w-md text-sm text-muted-foreground sm:mx-0">
                {c.tagline}
              </p>
            )}

            <div className={`mt-4 flex flex-col items-stretch justify-center gap-2.5 sm:flex-row sm:items-center sm:justify-start ${compact ? 'sm:mt-3' : ''}`}>
              <Button asChild data-testid="book-promo-buy-btn" size={compact ? 'sm' : 'default'} className="gap-2">
                <a
                  href={AMAZON_PAPERBACK}
                  target="_blank"
                  rel="noopener noreferrer sponsored"
                  onClick={() => trackBookClick(compact ? 'banner_top_buy' : 'banner_buy')}
                >
                  <ShoppingCart className="h-4 w-4" />
                  {isDe ? 'Jetzt bestellen' : 'Order now'}
                </a>
              </Button>
              <Button
                variant="outline"
                size={compact ? 'sm' : 'default'}
                onClick={() => { trackBookClick(compact ? 'banner_top_more' : 'banner_more'); goToBookPage(); }}
                data-testid="book-promo-more-btn"
                className="gap-2"
              >
                {isDe ? 'Mehr erfahren & Leseprobe' : 'Learn more & sample'}
                <ArrowRight className="h-4 w-4" />
              </Button>
              {!compact && (
                <Button
                  variant="ghost"
                  onClick={() => { trackBookClick('banner_all_shops'); setOpen(true); }}
                  data-testid="book-promo-all-options-btn"
                  className="gap-2"
                >
                  {isDe ? 'Alle Shops' : 'All shops'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="book-promo-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{BOOK.title}</DialogTitle>
            <DialogDescription>
              {isDe
                ? `von ${BOOK.author} \u2013 wähle dein Format und deinen Wunsch-Shop.`
                : `by ${BOOK.author} \u2013 choose your format and preferred shop.`}
            </DialogDescription>
          </DialogHeader>
          <BookOrderOptions isDe={isDe} />
        </DialogContent>
      </Dialog>
    </section>
  );
};

// Shared block listing every format and its retailers (used in dialog + page).
// `source` ('banner' | 'landing') is folded into the shop click target so the
// admin conversion can distinguish book-page orders from banner-dialog orders.
export const BookOrderOptions = ({ isDe, source = 'banner' }) => (
  <div className="space-y-5" data-testid="book-order-options">
    {FORMATS.map((fmt) => {
      const Icon = FORMAT_ICONS[fmt.id] || BookOpen;
      const loc = isDe ? fmt.de : fmt.en;
      return (
        <div key={fmt.id} data-testid={`book-format-${fmt.id}`}>
          <div className="mb-2 flex items-start gap-2.5">
            <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Icon className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <h4 className="text-sm font-semibold text-foreground">{loc.name}</h4>
              <p className="text-xs text-muted-foreground">{loc.note}</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground/70">{fmt.ids}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 pl-10">
            {fmt.shops.map((shop) => (
              <a
                key={shop.url}
                href={shop.url}
                target="_blank"
                rel="noopener noreferrer sponsored"
                onClick={() => trackBookClick(`shop_${source}_${fmt.id}_${shop.name.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`)}
                data-testid={`book-shop-${fmt.id}-${shop.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className="group inline-flex items-center gap-1.5 rounded-lg border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors duration-150 hover:border-primary hover:bg-secondary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {shop.name}
                <ExternalLink className="h-3 w-3 text-muted-foreground transition-colors group-hover:text-primary" />
              </a>
            ))}
          </div>
        </div>
      );
    })}
    <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground/70">
      {isDe
        ? 'Hinweis: Du wirst zu externen Shops weitergeleitet. Verfügbarkeit und Preise können variieren.'
        : 'Note: you will be redirected to external shops. Availability and prices may vary.'}
    </p>
  </div>
);

export default BookPromoBanner;
